<script lang="ts">
	import { page } from '$app/state';
	import Chip from '$lib/components/atoms/chip/Chip.svelte';
	import { poiCategoryChipSchemes, poiCategoryIcons } from '$lib/components/pois/constants';
	import {
		mainPoiCategoryList,
		type PoiCategory,
		poiCategoryList,
	} from '$lib/components/pois/types';
	import { getSponsorConfigByPoiCategory, isAnyPoiCategoryPartner, sponsoredPoiTracking } from '$lib/constants/sponsoredPoi';
	import * as cm from '$lib/i18n/computedMessages';
	import * as m from '$paraglide/messages';

	interface Props {
		selectedPoiCategories: PoiCategory[];
		displayAllCategories?: boolean;
		singleCategoryMode?: boolean;
		withPartner?: boolean;
		withLabel?: boolean;
		onChange?: (categories: PoiCategory[], info: {
			poiCategory: PoiCategory;
			selected: boolean;
			singleCategoryMode: boolean;
		}) => void;
	}

	const {
		selectedPoiCategories,
		displayAllCategories = false,
		singleCategoryMode = false,
		withPartner = false,
		withLabel = false,
		onChange,
	}: Props = $props();

	function updateSelectedCategory(poiCategory: PoiCategory, selected: boolean) {
		let updatedSelectedCategories = [...selectedPoiCategories];

		if (singleCategoryMode) {
			updatedSelectedCategories = selected ? [poiCategory] : [];
		} else {
			if (selected) {
				if (!updatedSelectedCategories.includes(poiCategory)) {
					updatedSelectedCategories.push(poiCategory);
				}
			} else {
				updatedSelectedCategories = updatedSelectedCategories.filter(
					category => category !== poiCategory,
				);
			}
		}

		onChange?.(updatedSelectedCategories, {
			singleCategoryMode,
			poiCategory,
			selected,
		});
	}

	const poiCategoriesList = $derived((displayAllCategories ? poiCategoryList : mainPoiCategoryList).filter(
		(category) => {
			if (isAnyPoiCategoryPartner(category)) {
				return withPartner;
			}

			return true;
		},
	));
</script>

<div
	class="flex gap-1 flex-wrap"
	role="listbox"
	aria-multiselectable={!singleCategoryMode}
	aria-orientation="horizontal"
	aria-label={m.poi_category_settings__header()}
>
	{#each poiCategoriesList as poiCategory (poiCategory)}
		{@const selected = selectedPoiCategories.includes(poiCategory)}
		{#if isAnyPoiCategoryPartner(poiCategory)}
			{@const partnerChip = getSponsorConfigByPoiCategory(poiCategory)?.chip}
			{#if partnerChip}
				<Chip
					text={withLabel ? partnerChip.text : ''}
					imgUrl={selected ? partnerChip.selected.imgUrl : partnerChip.unselected.imgUrl}
					selectedColorStyles={partnerChip.selected.style}
					{selected}
					imgSize={partnerChip.imgSize || 'md'}
					ariaLabel={withLabel ? '' : `${m.poi_search_category__aria_label()} ${partnerChip.text}`}
					onSelectedChange={(selected) => {
						if (partnerChip.tracking) {
							sponsoredPoiTracking(selected, page.route, partnerChip.tracking);
						}
						updateSelectedCategory(poiCategory, selected);
					}}
				/>
			{/if}
		{:else}
			<Chip
				text={withLabel ? cm.poiCategories(poiCategory) : ''}
				scheme={poiCategoryChipSchemes[poiCategory]}
				icon={poiCategoryIcons[poiCategory]}
				{selected}
				ariaLabel={withLabel ? '' : `${m.poi_search_category__aria_label()} ${cm.poiCategories(poiCategory)}`}
				onSelectedChange={(selected) => {
					updateSelectedCategory(poiCategory, selected);
				}}
			/>
		{/if}
	{/each}
</div>
